import { Minus, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRemoveRandomTally } from '~/api';
import { useSubmitVehicleCapacity } from '~/hooks/useSubmitVehicleCapacity';
import { useCannedTallyGroup } from '~/hooks/useCannedTallyGroup';
import type { OrderItem, UnitCode } from '~/graphql/graphql';
import { PricePerPieceTooltip, LoadsCoveredLabel } from '~/components/ui';
import { VehicleCapacityEditor } from '~/components/ui/VehicleCapacityEditor';
import type { TransportMode } from '~/hooks/useTransportState';
import {
  formatCurrency,
  roundTo6dp,
  roundToCents,
  toastGraphQLError,
} from '~/utils';
import {
  getGradeInformation,
  getItemWholeFt,
  getListedPricePerPiece,
  getRandomTallyBundleCount,
  getRandomTallyTotalPieces,
  getUnitsPerBundle,
  getVehiclePayload,
} from '~/utils/order';

interface CartTallyGroupProps {
  items: OrderItem[];
  tallyPlatformProductId: string;
  isRandomTally: boolean;
  organizationId: string;
  cartId: string;
  locationId: string;
  sellingOrganizationFacilityId: string;
  selectedTransportVehicleId: string;
  transport?: TransportMode;
  parentOrderId?: string | null;
  isFromQuote: boolean;
  currency?: string | null;
  incoTerm?: string | null;
}

export function CartTallyGroup({
  items,
  tallyPlatformProductId,
  isRandomTally,
  organizationId,
  cartId,
  locationId,
  sellingOrganizationFacilityId,
  selectedTransportVehicleId,
  transport,
  parentOrderId,
  isFromQuote,
  currency: cartCurrency,
  incoTerm,
}: Readonly<CartTallyGroupProps>) {
  const { t } = useTranslation('order-builder');
  const { t: tCart } = useTranslation('cart');
  const { mutate: removeRandomTally } = useRemoveRandomTally();
  const submitVehicleCapacity = useSubmitVehicleCapacity();

  const handleCapacitySubmit =
    (platformProductLengthId: string, unit: UnitCode) => (value: number) =>
      submitVehicleCapacity({
        platformProductLengthId,
        transportVehicleId: selectedTransportVehicleId,
        value,
        unit,
      });

  const {
    tally,
    tallyBundlesByFt,
    standaloneItemIds,
    tallyCount,
    changeTallyCount,
    loadsCovered,
    isUpdating,
    removeTallyGroup,
  } = useCannedTallyGroup({
    items,
    tallyPlatformProductId: isRandomTally ? undefined : tallyPlatformProductId,
    organizationId,
    transportVehicleId: selectedTransportVehicleId || undefined,
    cartId,
    locationId,
    sellerOrganizationFacilityId: sellingOrganizationFacilityId,
    parentOrderId,
    isFromQuote,
  });

  const handleRemove = () => {
    if (!isRandomTally) {
      removeTallyGroup();
      return;
    }
    removeRandomTally(
      { cartId, tallyPlatformProductId },
      { onError: toastGraphQLError }
    );
  };

  return (
    <div
      className="border-b border-neut-200 last:border-b-0 px-4 sm:px-6 py-3"
      data-testid={
        isRandomTally ? 'cart-random-tally-group' : 'cart-canned-tally-group'
      }
    >
      <div className="border border-status-info-200 rounded-lg overflow-hidden">
        <div className="bg-status-info-50 px-4 sm:px-6 py-1.5 border-b border-status-info-200">
          <span className="text-xs font-semibold text-status-info-800">
            {isRandomTally
              ? t('order_builder.filters.random_tally')
              : t('order_builder.filters.canned_tally')}
          </span>
        </div>

        {!isRandomTally && (
          <div className="px-4 sm:px-6 py-3 border-b border-neut-100 flex items-center gap-1">
            <button
              type="button"
              aria-label="Decrease tally count"
              onClick={() => changeTallyCount(tallyCount - 1)}
              disabled={isFromQuote || isUpdating || !tally || tallyCount <= 1}
              className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Minus size={14} aria-hidden="true" />
            </button>
            <span
              data-testid="cart-canned-tally-count"
              className="text-sm font-semibold text-neut-900 w-12 text-center"
            >
              {tallyCount}
            </span>
            <button
              type="button"
              aria-label="Increase tally count"
              onClick={() => changeTallyCount(tallyCount + 1)}
              disabled={isFromQuote || isUpdating || !tally}
              className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} aria-hidden="true" />
            </button>
            <div className="ml-1 flex flex-col">
              <span className="text-xs text-neut-600">
                {t(
                  'order_builder.canned_tally_calculator.tallies'
                ).toLowerCase()}
              </span>
              <LoadsCoveredLabel
                loadsCovered={loadsCovered}
                transport={transport}
              />
            </div>
          </div>
        )}

        {items.map((item) => {
          const wholeFt = getItemWholeFt(item);
          const unitsPerBundle = getUnitsPerBundle(item);
          const tallyBundles =
            wholeFt === null ? undefined : tallyBundlesByFt[wholeFt];
          const isStandalone = standaloneItemIds.has(
            item.platformProductsLengths.id
          );
          const bundleCount = isRandomTally
            ? (getRandomTallyBundleCount(item) ?? undefined)
            : tallyBundles != null
              ? tallyCount * tallyBundles
              : undefined;
          const totalPieces = isRandomTally
            ? (getRandomTallyTotalPieces(item) ?? undefined)
            : undefined;
          const listedPricePerPiece = getListedPricePerPiece(item, incoTerm);
          const pricePerPiece =
            listedPricePerPiece != null
              ? roundTo6dp(listedPricePerPiece.amount)
              : null;
          return (
            <div
              key={item.platformProductsLengths?.id}
              className="p-4 sm:p-6 border-b border-neut-200 last:border-b-0"
            >
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg flex items-center justify-center shrink-0">
                  <div className="text-2xl" aria-hidden="true">
                    🌲
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-neut-prim font-semibold mb-1 truncate">
                        {item.platformProductsLengths.platformProduct.name}
                      </h4>
                      <p className="text-sm text-neut-sec mb-1">
                        {tCart('cart.cart_grade')}:{' '}
                        {getGradeInformation(item.platformProductsLengths)}
                        <span className="ml-2 px-2 py-1 bg-success-100 text-success-600 rounded-md">
                          {item.platformProductsLengths.lengthDisplayValue}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-neut-prim">
                        {item.quantity.value.toFixed(2)} {item.quantity.unit}
                        {bundleCount != null && (
                          <span className="text-neut-400 ml-1">
                            ({Math.round(bundleCount)}{' '}
                            {t('order_builder.canned_tally_calculator.bundles')}
                            {totalPieces != null &&
                              ` / ${Math.round(totalPieces)} ${t('order_builder.canned_tally_calculator.pcs')}`}
                            )
                          </span>
                        )}
                      </span>
                      {unitsPerBundle != null && (
                        <p className="text-xs text-neut-400 mt-0.5">
                          {unitsPerBundle.toFixed(2)} {item.quantity.unit}/
                          {t('order_builder.inventory.bundle')}
                        </p>
                      )}
                      {isStandalone && (
                        <p className="text-xs text-warning-600 mt-0.5">
                          {t(
                            'order_builder.canned_tally_calculator.standalone_length_hint'
                          )}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-neut-sec">
                        {item.effectivePrice?.amount != null
                          ? `${formatCurrency(item.effectivePrice.amount, cartCurrency ?? undefined)}/${item.quantity.unit}`
                          : tCart('cart.price_not_available')}
                      </p>
                      <p className="text-lg text-neut-prim font-semibold">
                        {item.effectivePrice?.amount != null
                          ? formatCurrency(
                              roundToCents(
                                item.effectivePrice.amount * item.quantity.value
                              ),
                              cartCurrency ?? undefined
                            )
                          : tCart('cart.price_not_available')}
                      </p>
                      {pricePerPiece != null && (
                        <PricePerPieceTooltip
                          pricePerPiece={pricePerPiece}
                          currency={cartCurrency ?? undefined}
                          className="text-xs font-semibold text-neut-sec justify-end"
                        />
                      )}
                    </div>
                  </div>
                  {isRandomTally && (
                    <VehicleCapacityEditor
                      platformProductsLength={item.platformProductsLengths}
                      quantityUnit={item.quantity.unit}
                      vehiclePayload={getVehiclePayload(
                        item.platformProductsLengths
                          .platformProductVehicleCapacities,
                        selectedTransportVehicleId
                      )}
                      vehicleLabel={transport}
                      onCapacitySubmit={handleCapacitySubmit(
                        item.platformProductsLengths.id,
                        item.quantity.unit
                      )}
                      disabled={isFromQuote || !selectedTransportVehicleId}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="px-4 sm:px-6 py-3 border-t border-neut-100 flex justify-end">
          <button
            type="button"
            aria-label={
              isRandomTally
                ? t(
                    'order_builder.canned_tally_calculator.remove_random_tally_group'
                  )
                : t('order_builder.canned_tally_calculator.remove_tally_group')
            }
            onClick={handleRemove}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-error-600 border border-error-300 bg-error-50 hover:bg-error-100 rounded-lg transition-colors"
          >
            <Trash2 size={12} aria-hidden="true" />
            {isRandomTally
              ? t(
                  'order_builder.canned_tally_calculator.remove_random_tally_group'
                )
              : t('order_builder.canned_tally_calculator.remove_tally_group')}
          </button>
        </div>
      </div>
    </div>
  );
}
